import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createMentorConversation,
  deleteMentorConversation,
  getMentorConversation,
  listMentorConversations,
  streamMentorMessage,
} from '../services/mentorService';
import type { MentorConversationDetail, MentorReply } from '../services/mentorService';
import { invalidateAiUsage } from './useAiUsage';

export const mentorKeys = {
  list: (aquariumId: string) => ['mentor', aquariumId, 'conversations'] as const,
  detail: (aquariumId: string, conversationId: string) => ['mentor', aquariumId, 'conversation', conversationId] as const,
};

export function useMentorConversations(aquariumId: string) {
  return useQuery({
    queryKey: mentorKeys.list(aquariumId),
    queryFn: () => listMentorConversations(aquariumId),
    enabled: aquariumId !== '',
  });
}

export function useMentorConversation(aquariumId: string, conversationId: string | null) {
  return useQuery({
    queryKey: mentorKeys.detail(aquariumId, conversationId ?? ''),
    queryFn: () => getMentorConversation(aquariumId, conversationId ?? ''),
    enabled: aquariumId !== '' && conversationId !== null,
  });
}

export interface AskVariables {
  /** The open conversation, or null to start a new one with this question. */
  conversationId: string | null;
  content: string;
  includePhotos: boolean;
  /** Emergency mode: the incident the question is about (its checklist state joins the question). */
  incidentId?: string;
}

export interface AskResult {
  conversationId: string;
  reply: MentorReply;
}

/** Streamed text is pushed to React at most this often (ms), not once per delta. */
export const MENTOR_STREAM_FLUSH_MS = 50;

/**
 * Sends a question and streams the answer. Without an open conversation one is
 * created first; its id is reported through onConversationCreated even when
 * the answer then fails, so the user stays in it and can retry.
 *
 * Besides the mutation state it returns `streamText` (the answer received so
 * far, while pending), `receivedText` (whether any answer text arrived — to
 * tell a mid-stream failure apart) and `cancel()` (aborts the stream; the
 * mutation then fails with code MENTOR_STREAM_ABORTED and nothing is stored).
 */
export function useAskMentor(aquariumId: string, onConversationCreated: (id: string) => void) {
  const queryClient = useQueryClient();
  const [streamText, setStreamText] = useState('');
  const [receivedText, setReceivedText] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const bufferRef = useRef('');
  const flushTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = useCallback(() => {
    flushTimer.current = null;
    setStreamText(bufferRef.current);
  }, []);

  const onDelta = useCallback(
    (text: string) => {
      bufferRef.current += text;
      setReceivedText(true);
      flushTimer.current ??= setTimeout(flush, MENTOR_STREAM_FLUSH_MS);
    },
    [flush],
  );

  const stopFlushing = useCallback(() => {
    if (flushTimer.current !== null) clearTimeout(flushTimer.current);
    flushTimer.current = null;
  }, []);

  // Leaving the page cancels a running answer (nothing is stored server-side).
  useEffect(
    () => () => {
      abortRef.current?.abort();
      stopFlushing();
    },
    [stopFlushing],
  );

  const mutation = useMutation<AskResult, unknown, AskVariables>({
    mutationFn: async ({ conversationId, content, includePhotos, incidentId }) => {
      const controller = new AbortController();
      abortRef.current = controller;
      bufferRef.current = '';
      stopFlushing();
      setStreamText('');
      setReceivedText(false);
      let id = conversationId;
      if (id === null) {
        const created = await createMentorConversation(aquariumId);
        id = created.id;
        queryClient.setQueryData<MentorConversationDetail>(mentorKeys.detail(aquariumId, id), created);
        onConversationCreated(id);
      }
      const reply = await streamMentorMessage(aquariumId, id, content, includePhotos, {
        onDelta,
        signal: controller.signal,
        ...(incidentId ? { incidentId } : {}),
      });
      return { conversationId: id, reply };
    },
    onSuccess: ({ conversationId, reply }) => {
      queryClient.setQueryData<MentorConversationDetail>(mentorKeys.detail(aquariumId, conversationId), (old) =>
        old
          ? {
              ...old,
              title: old.title || reply.userMessage.content.slice(0, 120),
              messages: [...old.messages, reply.userMessage, reply.assistantMessage],
              messageCount: old.messageCount + 2,
            }
          : old,
      );
      void queryClient.invalidateQueries({ queryKey: mentorKeys.list(aquariumId) });
    },
    onSettled: () => {
      abortRef.current = null;
      stopFlushing();
      bufferRef.current = '';
      setStreamText('');
      invalidateAiUsage(queryClient);
    },
  });

  const cancel = useCallback(() => abortRef.current?.abort(), []);

  return { ...mutation, streamText, receivedText, cancel };
}

export function useDeleteMentorConversation(aquariumId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: string) => deleteMentorConversation(aquariumId, conversationId),
    onSuccess: (_data, conversationId) => {
      queryClient.removeQueries({ queryKey: mentorKeys.detail(aquariumId, conversationId) });
      void queryClient.invalidateQueries({ queryKey: mentorKeys.list(aquariumId) });
    },
  });
}
